import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

import ConfirmationDialog from './ConfirmationDialog';

import { ConfirmationDialog as ConfirmationDialogType } from '@/shared/types/confirmationDialog';

type ConfirmFunction = (dialog: ConfirmationDialogType) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFunction>(() => Promise.resolve(false));

export const useConfirm = (): ConfirmFunction => {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return confirm;
};

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dialog, setDialog] = useState<ConfirmationDialogType | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  useEffect(() => {
    return () => {
      // reject any in‐flight confirmation if the provider goes away
      resolverRef.current?.(false);
    };
  }, []);

  const confirm = useCallback<ConfirmFunction>((dialog) => {
    return new Promise<boolean>((resolve) => {
      if (resolverRef.current) {
        // optional: auto-reject any previous unresolved confirm
        resolverRef.current(false);
      }
      resolverRef.current = resolve;
      setDialog(dialog);
    });
  }, []);

  const handleClose = (ok: boolean) => {
    resolverRef.current?.(ok);
    resolverRef.current = null;
    setDialog(null);
  };
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <ConfirmationDialog
          open
          {...dialog}
          onConfirm={() => handleClose(true)}
          onCancel={() => handleClose(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
};
